<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { chargeClosuresApi, type ChargeClosure, type ChargeClosureLine } from '@/api/charge-closures';
import { formatCurrency } from '@/utils/currency';
import {
  closureStateChip, documentKindLabel, centsToAmount, taxDocumentProblem,
} from '@/utils/tax-documents';
import { formatShortDate } from '@/utils/patient-status';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// What a closure froze (its lines) and what the tax authority answered for it.
interface Props {
  modelValue: boolean;
  closureId: number | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>();

const { t } = useI18n();

const closure = ref<ChargeClosure | null>(null);
const lines = ref<ChargeClosureLine[]>([]);
const loading = ref(false);
const errorText = ref('');

async function load(id: number) {
  loading.value = true;
  errorText.value = '';
  try {
    const detail = await chargeClosuresApi.show(id);
    closure.value = detail.closure;
    lines.value = detail.lines;
  } catch {
    closure.value = null;
    lines.value = [];
    errorText.value = t('chargeClosures.detail.loadError');
  } finally {
    loading.value = false;
  }
}

watch(() => [props.modelValue, props.closureId] as const, ([open, id]) => {
  if (open && id) load(id);
}, { immediate: true });

function money(cents: number): string {
  return formatCurrency(centsToAmount(cents, closure.value?.currency ?? 'CLP'));
}

// The same single state the list shows, so one click never contradicts the row.
const closureChip = computed(() => (closure.value ? closureStateChip(closure.value, t) : null));
const document = computed(() => closure.value?.taxDocument ?? null);
const documentLabel = computed(() => (
  document.value ? documentKindLabel(document.value.kind, document.value.documentCode, t) : ''
));
// Our own sentence only; the provider's raw answer never leaves the server.
const documentProblem = computed(() => (document.value ? taxDocumentProblem(document.value, t) : ''));

const headers = [
  { title: t('chargeClosures.detail.date'), key: 'lineDate' },
  { title: t('chargeClosures.detail.description'), key: 'description' },
  { title: t('chargeClosures.detail.quantity'), key: 'quantity', align: 'end' as const },
  { title: t('chargeClosures.detail.unitPrice'), key: 'unitPriceCents', align: 'end' as const },
  { title: t('chargeClosures.detail.amount'), key: 'amountCents', align: 'end' as const },
];
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    :max-width="900"
    :title="t('chargeClosures.detail.title')"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #default>
      <div
        v-if="loading"
        class="d-flex justify-center pa-4"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
      <v-alert
        v-else-if="errorText"
        type="error"
        variant="tonal"
        density="compact"
      >
        {{ errorText }}
      </v-alert>
      <template v-else-if="closure">
        <div class="d-flex flex-wrap align-center ga-2 mb-2">
          <span class="text-h6">{{ closure.patientName }}</span>
          <v-chip
            v-if="closureChip"
            :color="closureChip.color"
            size="small"
          >
            {{ closureChip.label }}
          </v-chip>
        </div>
        <div class="text-body-2 mb-3">
          {{ t('chargeClosures.detail.period', {
            from: formatShortDate(closure.periodStart), to: formatShortDate(closure.periodEnd),
          }) }}
          · {{ t('chargeClosures.detail.recipient', {
            name: closure.recipientName ?? '', taxId: closure.recipientTaxId,
          }) }}
        </div>

        <v-alert
          v-if="document"
          :type="document.status === 'accepted' ? 'success' : 'info'"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          <div class="d-flex flex-wrap align-center ga-2">
            <span class="font-weight-medium">
              {{ documentLabel }}
              <span v-if="document.number">N° {{ document.number }}</span>
            </span>
            <span>{{ money(document.totalCents) }}</span>
          </div>
          <div
            v-if="document.roundingDeltaCents !== 0"
            class="text-body-2 mt-1"
          >
            {{ t('chargeClosures.detail.roundingDelta', {
              delta: money(Math.abs(document.roundingDeltaCents)),
            }) }}
          </div>
          <div
            v-if="documentProblem"
            class="text-body-2 mt-1"
          >
            {{ documentProblem }}
          </div>
          <div
            v-if="document.deliveredAt"
            class="text-body-2 mt-1"
          >
            {{ t('chargeClosures.detail.deliveredAt', { date: formatShortDate(document.deliveredAt) }) }}
          </div>
        </v-alert>
        <p
          v-else
          class="text-body-2 text-medium-emphasis mb-3"
        >
          {{ t('chargeClosures.detail.noDocument') }}
        </p>

        <v-data-table
          :headers="headers"
          :header-props="{ class: 'font-weight-bold' }"
          :items="lines"
          density="compact"
          items-per-page="-1"
          hide-default-footer
        >
          <template #item.lineDate="{ item }">
            {{ formatShortDate(item.lineDate) }}
          </template>
          <template #item.unitPriceCents="{ item }">
            {{ money(item.unitPriceCents) }}
          </template>
          <template #item.amountCents="{ item }">
            {{ money(item.amountCents) }}
          </template>
          <template #body.append>
            <tr class="font-weight-bold">
              <td :colspan="headers.length - 1">
                {{ t('chargeClosures.detail.total') }}
              </td>
              <td class="text-right">
                {{ money(closure.totalAmountCents) }}
              </td>
            </tr>
          </template>
        </v-data-table>
      </template>
    </template>
    <template #actions="{ close }">
      <v-btn
        variant="text"
        @click="close"
      >
        {{ t('chargeClosures.close') }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
